import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { ArrowTopRightOnSquareIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy, tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

/* Strip backgrounds/borders from Prism themes so code blends with artifact panel */
const coyNoBg = {
  ...coy,
  'pre[class*="language-"]': { ...coy['pre[class*="language-"]'], backgroundColor: 'transparent', background: 'none' },
  'pre[class*="language-"] > code': {
    ...coy['pre[class*="language-"] > code'],
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    borderLeft: 'none'
  },
  'pre[class*="language-"]:before': { display: 'none' },
  'pre[class*="language-"]:after': { display: 'none' }
};
const tomorrowNoBg = {
  ...tomorrow,
  'pre[class*="language-"]': { ...tomorrow['pre[class*="language-"]'], backgroundColor: 'transparent', background: 'none' }
};

/**
 * Displays an HTML chart file as an artifact with Preview (iframe) and Code tabs.
 * Like Claude's chart presentation - run command → generate file → show as artifact.
 */
const HtmlChartArtifact = ({ chatId, messageId, generatedFile, darkMode }) => {
  const iframeRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(320);
  const [isChartArtifact, setIsChartArtifact] = useState(true);

  useEffect(() => {
    if (!chatId || !messageId) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${messageId}/download-file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'
      })
      .then((res) => {
        setHtmlContent(res.data);
        setError(null);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load chart');
      })
      .finally(() => setLoading(false));
  }, [chatId, messageId]);

  const handleOpenInNewTab = async () => {
    // Open the dedicated public share URL (badge is rendered there only).
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/public/artifact/${messageId}/url`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { chatId }
        }
      );
      if (res?.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch (err) {
      console.warn('Failed to resolve public artifact URL, falling back to blob URL', err);
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${messageId}/file-url`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res?.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch (err) {
      console.warn('Failed to resolve hosted artifact URL, falling back to blob URL', err);
    }

    if (!htmlContent) return;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownload = async () => {
    if (!htmlContent) {
      return;
    }
    setDownloading(true);

    // Build a print-ready version of the HTML (white bg, dark text) without
    // touching the visible iframe at all
    const printStyle = `<style>
      html, body { background: #ffffff !important; background-color: #ffffff !important; color: #1a1a1a !important; }
      h1, h2, h3, h4, h5, h6 { color: #1a1a1a !important; }
      #chartLegend span, #chartLegend li, #chartLegend label { color: #1a1a1a !important; }
      .container { background: #ffffff !important; }
    </style>`;
    const printHtml = htmlContent.includes('</head>')
      ? htmlContent.replace('</head>', `${printStyle}\n</head>`)
      : `${printStyle}\n${htmlContent}`;

    const printBlobUrl = URL.createObjectURL(new Blob([printHtml], { type: 'text/html' }));

    // Create a hidden off-screen iframe for the print capture
    const hiddenIframe = document.createElement('iframe');
    hiddenIframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:800px;height:600px;visibility:hidden;border:none;';
    hiddenIframe.src = printBlobUrl;
    document.body.appendChild(hiddenIframe);

    try {
      await new Promise((resolve, reject) => {
        hiddenIframe.onload = resolve;
        hiddenIframe.onerror = reject;
        setTimeout(resolve, 3000); // fallback timeout
      });
      // Give charts a moment to render
      await new Promise((r) => setTimeout(r, 500));

      // Sync current chart colors from the live iframe into the print iframe
      // so any user-changed colors are reflected in the PDF
      try {
        const liveChartData = iframeRef.current?.contentWindow?.__getChartData?.();
        const printChart = hiddenIframe.contentWindow?.__chartInstance;
        if (liveChartData?.datasets && printChart) {
          liveChartData.datasets.forEach((ds, i) => {
            if (!printChart.data.datasets[i]) return;
            if (ds.borderColor) printChart.data.datasets[i].borderColor = ds.borderColor;
            if (ds.backgroundColor) printChart.data.datasets[i].backgroundColor = ds.backgroundColor;
            if (ds.pointBackgroundColor) printChart.data.datasets[i].pointBackgroundColor = ds.pointBackgroundColor;
            if (ds.pointBorderColor) printChart.data.datasets[i].pointBorderColor = ds.pointBorderColor;
            // Patch element-level cache so colors reflect immediately
            try {
              const meta = printChart.getDatasetMeta(i);
              if (meta?.data) {
                meta.data.forEach((el, elIdx) => {
                  // Pie/doughnut: each arc has its own color in the backgroundColor array
                  if (Array.isArray(ds.backgroundColor) && ds.backgroundColor[elIdx]) {
                    el.options.backgroundColor = ds.backgroundColor[elIdx];
                  } else if (ds.pointBackgroundColor) {
                    el.options.backgroundColor = ds.pointBackgroundColor;
                  }
                  if (ds.pointBorderColor) el.options.borderColor = ds.pointBorderColor;
                  else if (ds.borderColor) el.options.borderColor = ds.borderColor;
                });
              }
            } catch (e) { /* ignore */ }
          });
          printChart.update('none');
          await new Promise((r) => setTimeout(r, 200));
        }
      } catch (e) { /* non-fatal — PDF will use original colors */ }

      const dataUrl = await toPng(hiddenIframe.contentDocument.body, {
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;
      const scale = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      const w = imgWidth * scale;
      const h = imgHeight * scale;
      const x = (pageWidth - w) / 2;
      const y = (pageHeight - h) / 2;
      pdf.addImage(dataUrl, 'PNG', x, y, w, h);
      const baseName = (generatedFile?.fileName || 'chart').replace(/\.html?$/i, '');
      pdf.save(`${baseName}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
      console.error('PDF export failed');
    } finally {
      document.body.removeChild(hiddenIframe);
      URL.revokeObjectURL(printBlobUrl);
      setDownloading(false);
    }
  };

  const injectThemeStyles = useCallback((html, isDark) => {
    const textColor = isDark ? '#e5e7eb' : '#1f2937';
    const style = `<style>
html, body {
  background: transparent !important;
  background-color: transparent !important;
  color: ${textColor} !important;
}
.container, [class*="container"], [class*="chart"] {
  background: transparent !important;
  background-color: transparent !important;
}
canvas {
  background: transparent !important;
}
h1, h2, h3, h4, h5, h6 {
  color: ${textColor} !important;
}
/* Override inline styles set by chart legend JS */
#chartLegend span,
#chartLegend li,
#chartLegend label {
  color: ${textColor} !important;
}
/* Tighten legend gap */
#chartLegend {
  margin-top: 12px !important;
  gap: 12px !important;
}
/* Reduce container padding */
.container {
  padding: 20px 24px 12px !important;
}
</style>`;
    return html.includes('</head>')
      ? html.replace('</head>', `${style}\n</head>`)
      : `${style}\n${html}`;
  }, []);

  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (htmlContent) {
      setIframeLoaded(false);
      setIframeHeight(320);
      setIsChartArtifact(true);
      const processedHtml = injectThemeStyles(htmlContent, darkMode);
      const url = URL.createObjectURL(new Blob([processedHtml], { type: 'text/html' }));
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    setIframeLoaded(false);
  }, [htmlContent, darkMode, injectThemeStyles]);

  const updateIframeHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;
    const body = iframe.contentDocument.body;
    const html = iframe.contentDocument.documentElement;
    const height = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html?.scrollHeight || 0,
      html?.offsetHeight || 0,
      280
    );
    setIframeHeight(height);
  }, []);

  useEffect(() => {
    if (!iframeLoaded || !iframeRef.current || !isChartArtifact) return;
    updateIframeHeight();
    const iframe = iframeRef.current;
    const body = iframe?.contentDocument?.body;
    if (!body) return;
    const ro = new ResizeObserver(() => updateIframeHeight());
    ro.observe(body);
    resizeObserverRef.current = ro;
    const timer = setTimeout(updateIframeHeight, 500);
    return () => {
      ro.disconnect();
      resizeObserverRef.current = null;
      clearTimeout(timer);
    };
  }, [iframeLoaded, isChartArtifact, updateIframeHeight]);

  const saveChartColors = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow?.__getChartData || !chatId || !messageId) return;
    const chartData = iframe.contentWindow.__getChartData();
    if (!chartData) return;
    const token = localStorage.getItem('token');
    axios
      .put(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${messageId}/generated-file`,
        { chartData },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .catch((err) => {
        console.error('Failed to save chart colors:', err);
      });
  }, [chatId, messageId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== 'chartColorsChanged') return;
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(saveChartColors, 500);
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [saveChartColors]);

  return (
    <div className="mt-3 border border-gray-200 dark:border-[#444444] rounded-2xl overflow-hidden bg-white dark:bg-[#1a1a1a] min-w-0">
      {/* Header: preview indicator + actions (Code tab commented out - users only care about preview) */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-gray-200 dark:border-[#444444] bg-gray-50 dark:bg-[#111111] min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex gap-1.5 flex-shrink-0">
            {/* Eye icon commented out - not needed
            <button
              type="button"
              title="Preview"
              className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200 dark:bg-[#333] text-gray-700 dark:text-gray-200"
              aria-label="Preview"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            */}
            {/* Code tab commented out - users only care about preview
            <button
              type="button"
              onClick={() => setView('code')}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                view === 'code'
                  ? 'bg-gray-200 dark:bg-[#333] text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#222]'
              }`}
            >
              <CodeBracketIcon className="w-4 h-4" />
            </button>
            */}
          </div>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={handleOpenInNewTab}
            disabled={!htmlContent}
            title="Open in new tab"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200 dark:bg-[#333] text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-[#444] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Open in new tab"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!htmlContent || !iframeLoaded || downloading}
            title="Download as PDF"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Download as PDF"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="min-w-0 bg-white dark:bg-[#0a0a0a]">
        {loading && (
          <div className="flex items-center justify-center h-40 py-8 text-gray-500 dark:text-gray-400">
            Loading chart...
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-40 py-8 text-red-500">
            {error}
          </div>
        )}
        {!loading && !error && previewUrl && (
          <div className="">
            <iframe
              ref={iframeRef}
              title="Chart preview"
              src={previewUrl}
              className="w-full border-0"
              style={{
                minHeight: 280,
                height: isChartArtifact ? iframeHeight : '75vh',
                maxHeight: isChartArtifact ? undefined : '75vh',
                background: 'transparent'
              }}
              allowTransparency="true"
              sandbox="allow-scripts allow-same-origin allow-forms"
              onLoad={() => {
                const iframe = iframeRef.current;
                const doc = iframe?.contentDocument;
                const win = iframe?.contentWindow;
                const looksLikeChart = Boolean(
                  win?.__getChartData ||
                  doc?.getElementById('myChart') ||
                  doc?.getElementById('chartLegend')
                );
                setIsChartArtifact(looksLikeChart);
                setIframeLoaded(true);
              }}
            />
          </div>
        )}
        {/* Code view commented out - users only care about preview
        {!loading && !error && view === 'code' && htmlContent && (
          <div className="html-chart-artifact-code overflow-y-auto overflow-x-hidden max-h-[500px] px-4 py-2 min-w-0">
            <SyntaxHighlighter
              language="html"
              style={darkMode ? tomorrowNoBg : coyNoBg}
              customStyle={{
                margin: 0,
                padding: 0,
                fontSize: 12,
                background: 'transparent',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
              codeTagProps={{
                style: {
                  background: 'transparent',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }
              }}
            >
              {htmlContent}
            </SyntaxHighlighter>
          </div>
        )}
        */}
      </div>
    </div>
  );
};

export default HtmlChartArtifact;
